import LayoutUtils from 'src/utils/layout_utils';
import {
  getRoommatesValue,
  getRoommatesDisplayValue,
  setRoommatesValue
} from '../roommates_utils';
import { syncQuestionSubquestions } from '../subquestions';
import { QuestionDisplayHelper } from './display_helper';
import { QuestionAnswerSaveHandler } from './save_handler';

/**
 * Inline question editor (text, select, roommates, date). Save on blur/change, sync subquestions, update unanswered count.
 */
export class QuestionEditor {
  constructor(questionWrap) {
    this.questionWrap = questionWrap;
    this.display = null;
    this.input = null;
    this.editorElement = null;
    this.isRoommates = false;
    this.placeholder = '';
    this.saveUrl = '';
    this.questionId = '';
    this.questionBlock = null;
    this.displayHelper = null;
    this.saveHandler = null;
  }

  init() {
    if (this.questionWrap.dataset.bound === 'true') {
      return;
    }

    this._discoverElements();
    if (!this._validate()) {
      return;
    }

    this.displayHelper = new QuestionDisplayHelper({
      questionWrap: this.questionWrap,
      display: this.display,
      input: this.input,
      editorElement: this.editorElement,
      isRoommates: this.isRoommates,
      placeholder: this.placeholder,
      getRoommatesValue
    });

    this.saveHandler = new QuestionAnswerSaveHandler({
      saveUrl: this.saveUrl,
      questionId: this.questionId
    });

    this._initDisplayAndSync();
    this._bindEvents();
    this.questionWrap.dataset.bound = 'true';
  }

  _discoverElements() {
    this.display = this.questionWrap.querySelector('.js-showing-view-question-display');
    const roommatesEditor = this.questionWrap.querySelector('.js-showing-view-roommates-editor');
    this.isRoommates = !!roommatesEditor;
    this.input = this.isRoommates ? null : this.questionWrap.querySelector('.js-showing-view-question-input');
    this.editorElement = this.isRoommates
      ? roommatesEditor
      : (this.input?.closest('.js-showing-view-question-answer-select') || this.input);
    this.placeholder = this.questionWrap.dataset.placeholder || 'Not answered';
    this.saveUrl = this.questionWrap.dataset.saveAnswerUrl;
    this.questionId = this.questionWrap.dataset.questionId;
    this.questionBlock = this.questionWrap.closest('.js-showing-view-block-spacer');
  }

  _validate() {
    if (!this.display || !this.editorElement || !this.saveUrl || !this.questionId) {
      return false;
    }
    if (!this.isRoommates && !this.input) {
      return false;
    }
    return true;
  }

  _stopEditing() {
    this.questionWrap.classList.remove('showing-view-popup__question-answer-wrap--editing');
    LayoutUtils.show(this.display);
    LayoutUtils.hide(this.editorElement);
  }

  _startEditing() {
    this.questionWrap.classList.add('showing-view-popup__question-answer-wrap--editing');
    LayoutUtils.hide(this.display);
    LayoutUtils.show(this.editorElement);
    if (this.isRoommates) {
      const firstInput = this.editorElement.querySelector('.js-showing-view-roommate-input');
      firstInput?.focus();
    } else {
      this.input.focus();
      if (typeof this.input.setSelectionRange === 'function' && this.input.tagName !== 'SELECT') {
        this.input.setSelectionRange(this.input.value.length, this.input.value.length);
      }
      this._openDatePickerIfNeeded();
    }
  }

  _openDatePickerIfNeeded() {
    if (typeof flatpickr === 'undefined') return;
    const opts = {
      dateFormat: 'M j, Y',
      appendTo: document.querySelector('.js-sm-modal') || document.body
    };
    if (this.input.classList.contains('js-showing-view-movedate-input')) {
      if (!this.input._flatpickr) {
        opts.minDate = this.input.dataset.minDate || null;
        opts.maxDate = this.input.dataset.maxDate || null;
        flatpickr(this.input, opts);
      }
      this.input._flatpickr.open();
    } else if (this.input.classList.contains('js-showing-view-customdate-input')) {
      if (!this.input._flatpickr) {
        flatpickr(this.input, opts);
      }
      this.input._flatpickr.open();
    }
  }

  _buildSaveContext() {
    return {
      getValueForRequest: () =>
        this.isRoommates ? JSON.stringify(getRoommatesValue(this.editorElement)) : this.input.value,
      getStoredValue: () => this.questionWrap.dataset.value || '',
      getCurrentDisplayValue: () =>
        this.isRoommates ? getRoommatesDisplayValue(this.editorElement) : this.input.value,
      updateDisplay: (raw) => this.displayHelper.updateDisplay(raw),
      stopEditing: () => this._stopEditing(),
      isRoommates: this.isRoommates,
      editorElement: this.editorElement,
      display: this.display,
      getDisplayText: (raw) => this.displayHelper.getDisplayText(raw),
      getRoommatesDisplayValue: () => getRoommatesDisplayValue(this.editorElement),
      questionBlock: this.questionBlock,
      questionWrap: this.questionWrap
    };
  }

  _save() {
    this.saveHandler.save(this._buildSaveContext());
  }

  _initDisplayAndSync() {
    if (this.isRoommates) {
      const roommatesValue = getRoommatesValue(this.editorElement);
      this.questionWrap.dataset.value = JSON.stringify(roommatesValue);
      this.display.textContent = this.displayHelper.getDisplayText(getRoommatesDisplayValue(this.editorElement));
    } else {
      this.questionWrap.dataset.value = this.input.value || '';
      this.display.textContent = this.displayHelper.getDisplayText(this.questionWrap.dataset.value);
    }
    syncQuestionSubquestions(this.questionBlock, this.questionId, this.questionWrap.dataset.value);
  }

  _bindEvents() {
    this.display.addEventListener('click', () => this._startEditing());
    this.display.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._startEditing();
      }
    });

    if (this.isRoommates) {
      this.editorElement.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.editorElement.contains(document.activeElement)) {
            this._save();
          }
        }, 0);
      });
      this.editorElement.querySelectorAll('.js-showing-view-roommate-input').forEach((roommateInput) => {
        roommateInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            try {
              const restored = JSON.parse(this.questionWrap.dataset.value || '{}');
              setRoommatesValue(this.editorElement, restored);
            } catch {
              // Keep the current editor value if the saved roommate payload is malformed.
            }
            this._stopEditing();
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            roommateInput.blur();
          }
        });
      });
    } else {
      this.input.addEventListener('blur', () => this._save());
      if (this.input.tagName === 'SELECT') {
        this.input.addEventListener('change', () => this._save());
      }
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.input.value = this.questionWrap.dataset.value || '';
          this._stopEditing();
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          this.input.blur();
        }
      });
    }
  }
}
